import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import logger from '../../../../utils/logger';
// ExcelJS is dynamically imported when needed to reduce initial bundle size
import { usePersistedDataStore } from '../../../store';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import { toast } from '../../../../common/toast';
// pdf-lib is only used in WorkupDownload module, not directly here
import { showModalAndWaitForSelection} from '../../../../common/ShowModalAndWaitForSelection.js';
import { useNYData } from '../../../../common/NyDataObject.js';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';
import { useUser } from '../../../UserContext';
import { getTownViewXLSKeys, INTERNAL_XLS_KEYS } from '../config/columns';
import { getWorkupReportTypeIds } from '../../../../common/mappings.js';
import { uploadFileToS3Auto } from '../../Offers/s3UploadService.js';
import { downloadMergedWorkupsFromS3, downloadWorkupsFromS3 } from '../../Workups/WorkupDownload';
import { generateXLSData } from '../../Offers/s3UploadService.js';
import { useOfferOptionsStore } from '../../Offers/offerOptionsStore.js';
import { getSpreadsheetConfirmPayload } from '../../Offers/SendSpreadsheetOffersDialog.js';
import { downloadStips, DOWNLOAD_STIPS_OPTION } from '../../Settlement/downloadStips.js';
import DownloadWorkupsDialog from './DownloadWorkupsDialog.js';
import StipDownloadDialog from './StipDownloadDialog.js';
import SpreadsheetDownloadDialog from './SpreadsheetDownloadDialog.js';
import BulkDownloadEvidenceDialog from '../../NyscefUpload/BulkDownloadEvidenceDialog.js';

// Helper function to sanitize filename for S3 upload
const sanitizeFilenameForS3 = (filename) => {
  return filename
    .replace(/[^a-zA-Z0-9._-]/g, '_') // Replace any non-alphanumeric chars except ._- with underscore
    .replace(/_{2,}/g, '_') // Replace multiple consecutive underscores with single underscore
    .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
};

// Helper function to format court date as mm_dd_yy
const formatCourtDate = (courtDate) => {
  if (!courtDate || courtDate === 'All') {
    return '';
  }
  
  try {
    // Parse the court date - it could be in various formats
    let date;
    if (typeof courtDate === 'string') {
      // Try parsing as ISO date first
      if (courtDate.includes('T') || courtDate.includes('-')) {
        date = new Date(courtDate);
      } else {
        // Try parsing as MM/DD/YYYY or MM-DD-YYYY
        const dateStr = courtDate.replace(/[-/]/g, '/');
        date = new Date(dateStr);
      }
    } else {
      date = new Date(courtDate);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      logger.warn('Invalid court date:', courtDate);
      return '';
    }
    
    // Format as mm_dd_yy
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    
    return `${month}_${day}_${year}`;
  } catch (error) {
    logger.warn('Error formatting court date:', courtDate, error);
    return '';
  }
};

const DownloadButton = forwardRef(function DownloadButton({
  caseData,
  fileName,
  selectedRows,
  townView,
  uploadToS3 = false,
  villageMode,
  rows,
  isDataLoading,
  onUploadComplete,
  courtDate,
  avToggleActive = false,
  caseType,
  isWorkupRegenerating = false,
  casesStillRunning = false,
  showTrigger = true,
  onLoadingChange,
}, ref) {
  const getCompSheet = usePersistedDataStore((state) => state.compSheet);
  const negotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const { user } = useUser();
  const allowWorkupDownload =
    Boolean(user?.shareWorkups) && Boolean(user?.combinedFileDownload);
  const { getMunicipalityFromParcelId } = useNYData();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [workupsDialogOpen, setWorkupsDialogOpen] = useState(false);
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false);
  const [sheetChoiceOpen, setSheetChoiceOpen] = useState(false);
  const [stipsDialogOpen, setStipsDialogOpen] = useState(false);
  const includeCompsRef = useRef(true);
  const menuOpen = Boolean(anchorEl);

  const viewConfig = {
    townView: {
      showMenu: true,
      defaultOption: null,
      tooltipTitle: 'Download',
      includeCaseNotes: false,
      filterScarOnly: false,
    },
    regularView: {
      showMenu: true,
      defaultOption: null,
      tooltipTitle: 'Download Cases',
      includeCaseNotes: true,
      filterScarOnly: false,
    },
  };

  const currentConfig = viewConfig[townView ? 'townView' : 'regularView'];

  const getFilteredData = () => rows || caseData;

  const handleTownAllWorkupsDownload = async () => {
    setLoading(true);
    try {
      const typeOutput = user?.showOnePagers === false ? 'fnmaSummary' : 'onepagermaps';
      await downloadMergedWorkupsFromS3({
        filteredData: getFilteredData(),
        selectedRows,
        getCompSheet,
        negotiationObj,
        typeOutput,
        onLoadingChange: setLoading,
      });
    } catch (error) {
      logger.error('Error downloading town workups:', error);
      setLoading(false);
    }
  };

  const handleWorkupsDownload = async ({ workupReportType, combinePdfs }) => {
    setLoading(true);
    try {
      const filteredData = getFilteredData();
      await downloadWorkupsFromS3({
        selectedRows,
        filteredData,
        getCompSheet,
        typeOutput: workupReportType,
        combinePdfs,
        userId: user?.userId,
        onLoadingChange: (l) => {
          if (!l) setLoading(false);
        },
      });
    } catch (error) {
      logger.error('Error generating PDF report:', error);
      setLoading(false);
    }
  };

  const processDownloadOption = async (option) => {
    setAnchorEl(null);
    if (option === DOWNLOAD_STIPS_OPTION) {
      setStipsDialogOpen(true);
      return;
    }
    if (option === 'Download_All_Workups') {
      await handleTownAllWorkupsDownload();
      return;
    }
    if (option === 'Download_Workups') {
      setWorkupsDialogOpen(true);
      return;
    }
    if (option === 'Download_Evidence') {
      setEvidenceDialogOpen(true);
      return;
    }
    if (option === 'New_Template' || option === 'External') {
      setLoading(true);
      const filtered = getFilteredData();
      const selectedIds = new Set((selectedRows || []).map((row) => row?.parcel_id).filter(Boolean));
      const selectedFromGrid = selectedIds.size
        ? filtered.filter((row) => row && !row.loading && selectedIds.has(row.parcel_id))
        : [];
      const selectedOnly = (selectedRows || []).filter((row) => row && !row.loading && row.parcel_id);
      await downloadXLS(
        selectedFromGrid.length ? selectedFromGrid : (selectedOnly.length ? selectedOnly : filtered),
        option,
        undefined,
      );
      return;
    }
    if (!getWorkupReportTypeIds().includes(option)) {
      let filteredCases;
      const dataToFilter = getFilteredData();

      let baseFilteredCases;
      if (option === 'Selected_Cases') {
        baseFilteredCases = selectedRows || [];
      } else if (option === 'Scar_Only') {
        baseFilteredCases = dataToFilter.filter(
          (caseItem) =>
            caseItem.SCARFiled === 1 ||
            (caseItem.RepID && caseItem.RepID !== '' && caseItem.RepID != null)
        );
      } else {
        baseFilteredCases = dataToFilter;
      }

      if (uploadToS3) {
        const filteredParcelIds = new Set(baseFilteredCases.map((caseItem) => caseItem.parcel_id));
        baseFilteredCases = dataToFilter.map((caseItem) => {
          const isInFilteredSelection = filteredParcelIds.has(caseItem.parcel_id);
          if (isInFilteredSelection) return caseItem;
          if (!caseItem.isActive) return caseItem;
          const {
            OfferValue,
            RequestedAssessment,
            RequestedAV,
            PortfolioOffer,
            PortfolioAV,
            AventineNote,
            WorkupValue,
            ProprietyWorkup,
            ...sanitizedCase
          } = caseItem;
          return sanitizedCase;
        });
      }

      if (currentConfig.includeCaseNotes) {
        if (townView) {
          filteredCases = baseFilteredCases;
        } else {
          const selectedOption = await showModalAndWaitForSelection(
            'Select Option',
            'Do you want to include internal Case Notes in this download?',
            'Yes',
            'No'
          );
          filteredCases =
            selectedOption === 'Yes'
              ? baseFilteredCases
              : baseFilteredCases.map((caseItem) => ({ ...caseItem, CaseNotes: '' }));
        }
      } else {
        filteredCases = baseFilteredCases.map((caseItem) => ({ ...caseItem, CaseNotes: '' }));
      }

      setLoading(true);
      await downloadXLS(filteredCases, option, undefined);
    } else {
      setLoading(true);
      await downloadXLS(getFilteredData(), option, undefined);
    }
  };

  const downloadXLS = async (dataToDownload, option, keysOverride) => {
    logger.log('[XLS Download] Starting download with store values:', {
      negotiationObj,
      villageRun: negotiationObj?.villageRun,
      villageMode,
      dataToDownloadLength: dataToDownload?.length,
    });

    const external = option === 'External';
    const offerOptions = useOfferOptionsStore.getState();
    const includeCompsInOffersSpreadsheet = external
      ? includeCompsRef.current
      : offerOptions.includeCompsInOffersSpreadsheet;
    const spreadsheetPayload = external ? getSpreadsheetConfirmPayload() : null;
    const workupOptions = spreadsheetPayload?.workupOptions;
    const exportAsTown = townView || external;

    const exportKeys =
      keysOverride?.length > 0
        ? keysOverride
        : exportAsTown
          ? getTownViewXLSKeys({
              ...(external ? {} : { user }),
              includeCompsInOffersSheet: includeCompsInOffersSpreadsheet,
              ...(workupOptions ? { workupOptions } : {}),
            })
          : [...INTERNAL_XLS_KEYS];

    setLoading(true);
    try {
      const blob = await generateXLSData(
        dataToDownload,
        null,
        exportKeys,
        exportAsTown,
        external ? negotiationObj?.villageRun === 1 : villageMode,
        external ? false : avToggleActive,
        external ? (negotiationObj?.caseType || 'Town') : caseType,
        {
          includeCompsInOffersSheet: includeCompsInOffersSpreadsheet,
          isRestricted: external || Boolean(townView),
          getMunicipalityFromParcelId,
          gridRows: rows,
          ...(townView && !external ? { user, includeTownRoundNotes: true } : {}),
          ...(external ? {
            workupOptions,
            blankUnsettledTownFields: true,
            workupReportTypeForXls: workupOptions?.type === 'individual'
              ? spreadsheetPayload.workupReportType
              : null,
          } : {}),
        }
      );

      let downloadFileName;
      if (external) {
        downloadFileName = `${fileName}_EXTERNAL.xlsx`;
      } else if (townView) {
        if (uploadToS3) {
          const muniString = negotiationObj?.muniString || 'Unknown';
          const formattedCourtDate = formatCourtDate(courtDate);
          const courtDateSuffix = formattedCourtDate ? `_${formattedCourtDate}` : '';
          let typeSuffix = '';
          if (caseType && caseType !== 'Town') {
            typeSuffix += `_${caseType}`;
          }
          const reportType = negotiationObj?.reportType;
          if (reportType === 'assessment') {
            typeSuffix += '_Assessment';
          }
          downloadFileName = `Aventine_${muniString}_Offers_Sent${courtDateSuffix}${typeSuffix}.xlsx`;
        } else {
          const muniString = negotiationObj?.muniString || 'Unknown';
          const currentDate = new Date();
          const month = String(currentDate.getMonth() + 1).padStart(2, '0');
          const day = String(currentDate.getDate()).padStart(2, '0');
          const year = String(currentDate.getFullYear()).slice(-2);
          downloadFileName = `Aventine_${muniString}_Offers_${month}_${day}_${year}.xlsx`;
        }
      } else {
        const suffix = option === 'New_Template' ? 'INTERNAL' : option;
        downloadFileName = `${fileName}_${suffix}.xlsx`;
      }

      if (uploadToS3) {
        try {
          const s3FileName = `Offersheets/${sanitizeFilenameForS3(downloadFileName)}`;
          const s3Url = await uploadFileToS3Auto(blob, s3FileName, 'negotiation-files');
          if (onUploadComplete) {
            onUploadComplete({ success: true, downloadUrl: s3Url, csvFilename: downloadFileName });
          }
        } catch (error) {
          logger.error('S3 upload failed:', error);
          toast.error(`S3 upload failed: ${error.message}`);
          if (onUploadComplete) {
            onUploadComplete({ success: false, error: error.message });
          }
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = downloadFileName;
          link.click();
        }
      } else {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = downloadFileName;
        link.click();
      }
    } catch (error) {
      logger.error('XLS download failed:', error);
      toast.error(error.message || 'Download failed');
    } finally {
      setLoading(false);
    }
  };

  const processRef = useRef(processDownloadOption);
  processRef.current = processDownloadOption;
  useImperativeHandle(ref, () => ({
    downloadCases: () => { setAnchorEl(null); setSheetChoiceOpen(true); },
    downloadWorkups: () => { void processRef.current('Download_Workups'); },
    downloadEvidence: () => { void processRef.current('Download_Evidence'); },
    downloadStips: () => { void processRef.current(DOWNLOAD_STIPS_OPTION); },
  }), []);

  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(loading);
  }, [loading]);

  return (
    <div style={showTrigger ? undefined : { display: 'contents' }}>
      {showTrigger && (
      <>
      <Tooltip
        title={casesStillRunning ? 'Cases are still running' : loading ? 'Downloading...' : currentConfig.tooltipTitle}
        placement="bottom"
        slotProps={{
          popper: { modifiers: [{ name: 'offset', options: { offset: [0, -5] } }] },
        }}
        open={tooltipOpen && !menuOpen}
        onOpen={() => setTooltipOpen(true)}
        onClose={() => setTooltipOpen(false)}
        disableFocusListener
        disableTouchListener
        disableInteractive
      >
        <span style={{ display: 'inline-flex' }}>
          <IconButton
            color="primary"
            size="small"
            aria-label="download"
            disabled={isDataLoading || loading || isWorkupRegenerating}
            sx={{
              p: 0.75,
              m: 0,
              width: 32,
              height: 32,
              borderRadius: '4px',
              lineHeight: 1,
              '& .MuiSvgIcon-root': { 
                color: isDataLoading || loading || isWorkupRegenerating ? '#ccc' : '#1976d2',
                fontSize: '1.2rem'
              },
              '&:hover': { 
                backgroundColor: isDataLoading || loading || isWorkupRegenerating ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                '& .MuiSvgIcon-root': { color: isDataLoading || loading || isWorkupRegenerating ? '#ccc' : '#1976d2' }
              },
              '&:active .MuiSvgIcon-root': { color: isDataLoading || loading || isWorkupRegenerating ? '#ccc' : '#1976d2' },
              '&.Mui-disabled': {
                backgroundColor: 'transparent !important'
              },
              '& .MuiCircularProgress-root': {
                color: '#1976d2'
              }
            }}
            onClick={(e) => {
              if (loading || isDataLoading) return; // Prevent clicking when loading
              setTooltipOpen(false);        // close tooltip immediately
              if (currentConfig.showMenu) {
                // Show menu for regular view
                setAnchorEl(e.currentTarget);
              } else {
                void processDownloadOption(currentConfig.defaultOption);
              }
            }}
          >
            {casesStillRunning || loading ? (
              <CircularProgress size={casesStillRunning ? 18 : 24} />
            ) : (
              <DownloadIcon sx={{  
                width: 24,
                height: 24,
              }}/>
            )}
          </IconButton>
        </span>
      </Tooltip>

      {currentConfig.showMenu && (
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={() => setAnchorEl(null)}
        >
          {townView ? (
            <>
              <MenuItem onClick={() => void processDownloadOption('All_Cases')}>
                Download Spreadsheet
              </MenuItem>
              {allowWorkupDownload && (
                <>
                  <Divider />
                  <MenuItem onClick={() => void processDownloadOption('Download_All_Workups')}>
                    Download All Workups
                  </MenuItem>
                </>
              )}
            </>
          ) : (
            <>
              <div style={{ padding: '0px 16px', fontWeight: 'bold' }}>XLS</div>
              <MenuItem onClick={() => { setAnchorEl(null); setSheetChoiceOpen(true); }}>Download Cases</MenuItem>
              <Divider />
              <div style={{ padding: '0px 16px', fontWeight: 'bold' }}>PDF</div>
              <MenuItem onClick={() => void processDownloadOption('Download_Workups')}>
                Download Workups
              </MenuItem>
              <MenuItem onClick={() => void processDownloadOption('Download_Evidence')}>
                Download Evidence
              </MenuItem>
              <Divider />
              <div style={{ padding: '0px 16px', fontWeight: 'bold' }}>Stipulations</div>
              <MenuItem onClick={() => void processDownloadOption(DOWNLOAD_STIPS_OPTION)}>
                Download Stips
              </MenuItem>
            </>
          )}
        </Menu>
      )}
      </>
      )}

      <StipDownloadDialog
        open={stipsDialogOpen}
        onClose={() => setStipsDialogOpen(false)}
        onConfirm={async (countersignedOnly) => {
          setStipsDialogOpen(false);
          setLoading(true);
          try {
            await downloadStips({ rows, selectedRows, countersignedOnly });
          } finally {
            setLoading(false);
          }
        }}
      />

      <SpreadsheetDownloadDialog
        open={sheetChoiceOpen}
        onClose={() => setSheetChoiceOpen(false)}
        onConfirm={({ external, includeComps }) => {
          includeCompsRef.current = includeComps;
          setSheetChoiceOpen(false);
          void processRef.current(external ? 'External' : 'New_Template');
        }}
      />

      <DownloadWorkupsDialog
        open={workupsDialogOpen}
        onClose={() => setWorkupsDialogOpen(false)}
        onConfirm={handleWorkupsDownload}
      />

      <BulkDownloadEvidenceDialog
        open={evidenceDialogOpen}
        onClose={() => setEvidenceDialogOpen(false)}
        selectedRows={selectedRows}
        rows={rows}
      />
    </div>
  );
});

export default DownloadButton;
